// ─── briefEnvelopeToFormState ─────────────────────────────────────────────
// Inverse of resolvePlatformAndFormat() in services/api/backendClient.js.
// Given a server-side BriefEnvelope (no FE-only activeMode/channel concepts),
// derive activeMode + socialChannel + emailChannel and delegate the rest to
// paramsToFormState. Used by the Examples Library "Carica nei campi" flow
// and forward-compat for Phase 2 personal saved runs.

import { paramsToFormState } from './paramsToFormState.js';

const SOCIAL_PLATFORMS = new Set([
  'instagram', 'facebook', 'tiktok', 'twitter', 'pinterest', 'youtube',
]);

function deriveFrontendMode(platform, contentFormat) {
  // linkedin + post|carousel → linkedin-post
  if (platform === 'linkedin' && (contentFormat === 'post' || contentFormat === 'carousel')) {
    return { activeMode: 'linkedin-post' };
  }
  // linkedin + newsletter → email mode w/ newsletter channel
  if (platform === 'linkedin' && contentFormat === 'newsletter') {
    return { activeMode: 'email', emailChannel: 'linkedin-newsletter' };
  }
  // blog + seo → blog-seo
  if (platform === 'blog' && contentFormat === 'seo') {
    return { activeMode: 'blog-seo' };
  }
  // blog + case-study → case-study
  if (platform === 'blog' && contentFormat === 'case-study') {
    return { activeMode: 'case-study' };
  }
  // email → email mode w/ email-classic channel (BE content_format == 'email-classic')
  if (platform === 'email') {
    return { activeMode: 'email', emailChannel: 'email-classic' };
  }
  // landing → landing
  if (platform === 'landing') {
    return { activeMode: 'landing' };
  }
  // ads + <channel> → ads (channel inferred from content_format)
  if (platform === 'ads') {
    return { activeMode: 'ads', _adsChannel: contentFormat };
  }
  // social platforms (instagram, facebook, …) + post → social mode
  if (SOCIAL_PLATFORMS.has(platform)) {
    return { activeMode: 'social', socialChannel: platform };
  }
  // Fallback: best effort
  return { activeMode: platform };
}

export function briefEnvelopeToFormState(brief) {
  if (!brief) return {};
  const { _adsChannel, ...fe } = deriveFrontendMode(brief.platform, brief.content_format);

  // For ads, paramsToFormState reconstructs adsParams.channel from
  // content_params.ads_channel — re-seed it from the BE-derived
  // content_format so the inverse mapping is exact.
  const content_params = _adsChannel
    ? { ...(brief.content_params || {}), ads_channel: _adsChannel }
    : brief.content_params;

  const synthetic = {
    ...brief,
    ...fe,
    ...(content_params !== undefined ? { content_params } : {}),
  };

  return paramsToFormState(synthetic);
}
